import { merge, memoize } from 'lodash';
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider, withTheme } from 'styled-components';
import { grey10, white } from '@zocdoc/branding/colors';
import { breakpoint } from '@zocdoc/components/src/grid';
import { filterToValidAttributes } from '@zocdoc/dom-property';

import AvailabilityFilter from '../AvailabilityFilter';
import GenderFilter from '../GenderFilter';
import TimeOfDayFilter from '../TimeOfDayFilter';
import OtherFilters from '../OtherFilters';
import Sorting from '../Sorting';
import isMobileBreakpoint from '../../../../../utils/isMobileBreakpoint';
import Header from '../Header';

import {
    RESULT_CARD_X_MARGIN_LARGE,
    RESULT_CARD_Y_MARGIN_XXSMALL,
    RESULT_CARD_Y_MARGIN_LARGE,
} from '../../../constants';

import { Defaults } from '../../../../constants';

import Languages from '../Languages';
import { VisitReasonsPanel } from '../VisitReasons';

const PanelWrapper = styled.div`
    padding-top: ${RESULT_CARD_Y_MARGIN_XXSMALL}px;
    padding-bottom: ${RESULT_CARD_Y_MARGIN_XXSMALL}px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    ${breakpoint.large} {
        padding-top: ${RESULT_CARD_Y_MARGIN_LARGE}px;
        padding-bottom: ${RESULT_CARD_Y_MARGIN_LARGE}px;
    }
`;

const SectionWrapper = styled.div`
    margin-bottom: ${RESULT_CARD_Y_MARGIN_XXSMALL}px;

    &:last-child {
        margin-bottom: 0;
    }

    ${breakpoint.small} {
        display: ${p => p.onlyMobile ? 'none' : 'block'};
        margin-left: 4px;
        margin-right: 4px;
    }

    ${breakpoint.large} {
        margin-left: ${RESULT_CARD_X_MARGIN_LARGE}px;
        margin-right: ${RESULT_CARD_X_MARGIN_LARGE}px;
    }
`;

const getAdjustedTheme = memoize(
    theme => merge({}, theme, {
        components: {
            CollapsibleSection: {
                header: {
                    arrowSize: 8,
                    bgColor: grey10,
                    bgColorExpanded: white,
                    fontSize: 14,
                    height: 46,
                },
            },
        },
    })
);

class Panel extends PureComponent {
    static propTypes = {
        address: PropTypes.string,
        carrierId: PropTypes.string,
        dayFilter: PropTypes.string.isRequired,
        directoryId: PropTypes.string,
        genderFilter: PropTypes.string.isRequired,
        onCloseCallback: PropTypes.func.isRequired,
        numberOfAppliedAvailabilityRefinements: PropTypes.number,
        numberOfAppliedGenderRefinements: PropTypes.number,
        numberOfAppliedHoursRefinements: PropTypes.number,
        numberOfAppliedLanguageRefinements: PropTypes.number,
        numberOfAppliedMiscRefinements: PropTypes.number,
        numberOfAppliedRefinements: PropTypes.number,
        pageOffset: PropTypes.number,
        planId: PropTypes.string,
        potentialMatches: PropTypes.number.isRequired,
        showProvidersWhoSeeChildren: PropTypes.bool.isRequired,
        showTimesAfter5: PropTypes.bool.isRequired,
        showTimesBefore10: PropTypes.bool.isRequired,
        sortType: PropTypes.string.isRequired,
        theme: PropTypes.object,
        updateParameters: PropTypes.func.isRequired,
    };

    render() {
        const {
            dayFilter,
            forceOpenPanel,
            genderFilter,
            languageId,
            onCloseCallback,
            numberOfAppliedAvailabilityRefinements,
            numberOfAppliedGenderRefinements,
            numberOfAppliedHoursRefinements,
            numberOfAppliedLanguageRefinements,
            numberOfAppliedMiscRefinements,
            numberOfAppliedRefinements,
            potentialMatches,
            procedureId,
            showProvidersWhoSeeChildren,
            showTimesAfter5,
            showTimesBefore10,
            sortType,
            specialtyId,
            updateParameters,
            visitReasons,
            ...props
        } = this.props;

        const isMobile = isMobileBreakpoint();
        const defaultOpen = false || forceOpenPanel;

        return (
            <div
                {...filterToValidAttributes(props)}
                data-test="search-refine-panel"
            >
                <Header
                    numberOfAppliedRefinements={numberOfAppliedRefinements}
                    onCloseCallback={onCloseCallback}
                    potentialMatches={potentialMatches}
                    resetCallback={() => updateParameters(Defaults)}
                />

                <ThemeProvider theme={getAdjustedTheme(props.theme)}>
                    <PanelWrapper>
                        <SectionWrapper onlyMobile>
                            <VisitReasonsPanel
                                defaultOpen={defaultOpen}
                                onChangeCallback={updateParameters}
                                procedureId={procedureId}
                                visitReasons={visitReasons}
                            />
                        </SectionWrapper>
                        <SectionWrapper>
                            <AvailabilityFilter
                                dayFilter={dayFilter}
                                defaultOpen={!isMobile || defaultOpen}
                                numApplied={numberOfAppliedAvailabilityRefinements}
                                onChangeCallback={updateParameters}
                            />
                        </SectionWrapper>
                        <SectionWrapper>
                            <TimeOfDayFilter
                                defaultOpen={defaultOpen}
                                numApplied={numberOfAppliedHoursRefinements}
                                onChangeCallback={updateParameters}
                                showTimesAfter5={showTimesAfter5}
                                showTimesBefore10={showTimesBefore10}
                            />
                        </SectionWrapper>
                        <SectionWrapper>
                            <GenderFilter
                                defaultOpen={defaultOpen}
                                genderFilter={genderFilter}
                                numApplied={numberOfAppliedGenderRefinements}
                                onChangeCallback={updateParameters}
                            />
                        </SectionWrapper>
                        <SectionWrapper>
                            <Languages
                                defaultOpen={defaultOpen}
                                languageId={languageId}
                                numApplied={numberOfAppliedLanguageRefinements}
                                onChangeCallback={updateParameters}
                            />
                        </SectionWrapper>
                        <SectionWrapper>
                            <OtherFilters
                                defaultOpen={defaultOpen}
                                numApplied={numberOfAppliedMiscRefinements}
                                onChangeCallback={updateParameters}
                                showProvidersWhoSeeChildren={showProvidersWhoSeeChildren}
                            />
                        </SectionWrapper>
                        <SectionWrapper>
                            <Sorting
                                defaultOpen={defaultOpen}
                                onChangeCallback={updateParameters}
                                sortType={sortType}
                            />
                        </SectionWrapper>
                    </PanelWrapper>
                </ThemeProvider>
            </div>
        );
    }
}

const ThemedPanel = withTheme(Panel);

export {
    ThemedPanel as default,
    ThemedPanel as Panel,
};
